import React, { useContext, useState } from 'react'
import { observer } from 'mobx-react'
import '../index.scss'
import ModalPortal from '@Components/Popups/ModalPortal'
import Sidebar from '@Components/Sidebars/Sidebar'
import AddWriter from '@Components/WorkRegistrationSubs/AddWriter'
import EditSimpleWriter from '@Components/WorkRegistrationSubs/EditSimpleWriter'
import { setItemToArrayByIndex, writerRemover } from '@App/helpers/work-entities/removers'
import { StoresContext } from '@App/contexts'
import { WorkModel } from '@Entities/work/work'
import RootStore from '@App/stores/root-store'
import { OptionModel } from '@Entities/lib'
import { writersTableSchema } from '@Entities/schemas/wizardFlow/writers-table-schema'
import TablesForm from '@Components/ViewWork/Table/TablesForm'
import WizardStepHeading from '@Components/WizardFlow/WizardStepHeading'
import { Link } from 'react-router'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import { WriterModel } from '@Entities/work/writer-model'
import { isComposerPresent } from '@App/helpers/work-entities/fields-validators'
import { useWriterTableRows } from '@Scenes/WorkScenes/Steps/WritersSteps/WritersStep/useWriterTableRows'

const WritersStep = () => {
    const { workStore, popupStore, libStore } = useContext<RootStore>(StoresContext)

    const [popupType, setPopupType] = useState<'addWriter' | 'editWriter'>()
    const [sidebarIsOpen, setSidebarIsOpen] = useState<boolean>(false)
    const [editingWriter] = useState<WriterModel>()

    const workDraftFromLocal = workStore.workDraftLocal
    const existingWorkHadComposer = workStore.workById ? isComposerPresent(workStore.workById.writers) : true

    if (!workDraftFromLocal) {
        return null
    }

    const removeWriter = (removingWriter: WriterModel) => {
        try {
            if (!workDraftFromLocal) {
                return
            }
            const updatedWorkDraft = writerRemover(removingWriter, workDraftFromLocal)
            workStore.setWorkDraftToLocal(updatedWorkDraft)
        } catch (e) {
            popupStore.setAlertFromError(e)
        }
    }

    const updateRole = (index: number, writerModel: WriterModel, newValue: OptionModel) => {
        const updatedWriterModel: WriterModel = { ...writerModel, roleCode: Number.parseInt(newValue.id, 10) }
        const updatedWritersArray = setItemToArrayByIndex(workDraftFromLocal.writers, index, updatedWriterModel)

        const updatedOP: OriginalPublisherModel[] = workDraftFromLocal.originalPublishers.map(
            (op: OriginalPublisherModel) => ({
                ...op,
                writers: op.writers.map((writer: WriterModel) =>
                    writer.localId === updatedWriterModel.localId
                        ? { ...writer, roleCode: updatedWriterModel.roleCode }
                        : writer
                ),
            })
        )

        const updatedWorkDraft: WorkModel = {
            ...workDraftFromLocal,
            writers: updatedWritersArray,
            originalPublishers: updatedOP,
        }

        workStore.setWorkDraftToLocal(updatedWorkDraft)
    }

    const stepValidator = (isValid: boolean) => {
        if (!workDraftFromLocal) {
            return
        }
        workStore.setWizardStepValidation(isValid)
    }

    const writerTableInfo = useWriterTableRows({
        writers: workDraftFromLocal.writers,
        roleOptions: libStore.writerRoles,
        onRoleChange: updateRole,
    })

    return (
        <div className="writers-step">
            <div className="step-content">
                <WizardStepHeading heading="Add Writers & Roles" title={workDraftFromLocal.title}>
                    <span>
                        Enter all writer(s) and their roles. A full list of writer roles and their definitions can be
                        found in our Help Center article{' '}
                        <Link
                            className="tooltip"
                            to="https://help.themlc.com/en/support/writer-roles"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            here.
                        </Link>
                    </span>
                    <span>
                        Please note: At least one Composer/Author or Composer writer role is required for all new
                        registrations.
                    </span>
                </WizardStepHeading>
                <TablesForm
                    headingDecoration={{ icon: 'writers', title: 'Writers', required: true }}
                    tableInfos={writerTableInfo}
                    addButtonTitle="Add Writer"
                    onAddButtonClicked={() => {
                        setPopupType('addWriter')
                        setSidebarIsOpen(true)
                    }}
                    onRemoveCellClicked={(index: number, removeCellIdentifier, reset, values) => {
                        removeWriter(workDraftFromLocal?.writers[index])
                        if (values && reset) {
                            values?.rows.splice(index, 1)
                            reset({ ...values })
                        }
                    }}
                    validationSchema={writersTableSchema(workDraftFromLocal.isComplete, existingWorkHadComposer)}
                    rowsAreValid={stepValidator}
                />
            </div>

            <ModalPortal isOpen={sidebarIsOpen} onClose={() => setSidebarIsOpen(false)}>
                <Sidebar onClose={() => setSidebarIsOpen(false)}>
                    {popupType === 'addWriter' ? <AddWriter closePopupFunc={() => setSidebarIsOpen(false)} /> : null}
                    {popupType === 'editWriter' && editingWriter ? (
                        <EditSimpleWriter
                            editingWriter={editingWriter}
                            closePopupFunc={() => setSidebarIsOpen(false)}
                        />
                    ) : null}
                </Sidebar>
            </ModalPortal>
        </div>
    )
}

export default observer(WritersStep)
